import { RenderKitPublic } from './RenderKitPublic'
import animGraphJson from '../assets/AnimGraph.json'
import animLogicJson from '../assets/animLogic.json'
import maleDefaults from '../assets/male_defaults'
import femaleDefaults from '../assets/female_defaults'

export class FURenderKitSDK extends RenderKitPublic {
    _resourceManager = undefined
    _modifiedRenderKit = undefined
    _modifiedRender = undefined

    get RenderKit() {
        if (!this._modifiedRenderKit) {
            this._modifiedRenderKit = this._modifyRenderKit(super.RenderKit)
        }
        return this._modifiedRenderKit
    }

    async initialize() {
        let engineAssetsUrl = 'FURenderKitSDK.data'
        if (this.wasmCtx.wasmOptions && this.wasmCtx.wasmOptions.locateFile) {
            engineAssetsUrl = this.wasmCtx.wasmOptions.locateFile(engineAssetsUrl)
        }
        const engineAssetsPromise = fetch(engineAssetsUrl).then(res => {
            if (!res.ok) {
                throw 'EngineAssets download failed'
            }
            return res.arrayBuffer()
        })

        await this.wasmCtx.LoadWasm()
        const buffer = await engineAssetsPromise
        const { ptr, size } = this.writeAssets(new Uint8Array(buffer))
        this.engineAssetsPtr = ptr
        this.engineAssetsSize = size
    }

    async authWithAuthpack(authpackUrl, authMethod) {
        const authpack = await this.LoadAssets(authpackUrl)
        this.wasmCtx.module.SetAuthpack(authpack.ptr, authpack.size)
        const requestStr = this.wasmCtx.module.GetRequestString('1.6.0')

        const resStr = await authMethod(requestStr)
        return this.wasmCtx.module.Auth(resStr)
    }

    _modifyRenderKit(RenderKit) {
        const getInstance = RenderKit.getInstance

        RenderKit.getInstance = () => {
            if (!this._modifiedRenderer) {
                this._modifiedRenderer = this._modifyRenderer(getInstance())
            }
            return this._modifiedRenderer
        }

        return RenderKit
    }

    _modifyRenderer(renderer) {
        let glCtx
        let sdk = this
        renderer.setFUResourceManager = mgr => {
            this._resourceManager = mgr
            return mgr.setModule(this.wasmCtx.module)
        }
        renderer.getFUResourceManager = () => {
            return this._resourceManager
        }

        const init = renderer.init.bind(renderer)
        renderer.init = ({ canvas }) => {
            if (!canvas) {
                throw 'A canvas should be provided'
            }

            sdk.wasmCtx.module.canvas = canvas

            return init(this.engineAssetsPtr, this.engineAssetsSize)
        }

        const createScene = renderer.createScene.bind(renderer)

        renderer.createScene = (name = 'default') => {
            return this._modifyScene(createScene(name))
        }

        renderer.createItemAsync = async function (path) {
            await self._resourceManager.prepareData(path)

            const result = this.createItemSync(path)
            self._resourceManager.markFileUsed(path)

            return result
        }

        const resize = renderer.resize.bind(renderer)
        renderer.resize = (width, height) => {
            const canvas = this.wasmCtx.module.canvas
            canvas.width = width
            canvas.height = height

            return resize(width, height)
        }

        renderer.getSize = () => {
            const canvas = this.wasmCtx.module.canvas
            return {
                width: canvas.width,
                height: canvas.height,
            }
        }

        renderer.captureImage = function (options, next) {
            const canvas = sdk.wasmCtx.module.canvas
            const oriWidth = canvas.width
            const oriHeight = canvas.height
            const { width, height, quality = 1, format = 'image/png', tmpCanvas } = options

            if (width && height && width !== oriWidth && height !== oriHeight) {
                if (!tmpCanvas) {
                    throw 'captureImage require a tmpCanvas to hold renderring result'
                }
                // 准备遮罩 canvas
                tmpCanvas.width = oriWidth
                tmpCanvas.height = oriHeight
                glCtx = tmpCanvas.getContext('2d')

                const parentNode = canvas.parentNode
                const nextSibling = canvas.nextSibling

                this.nextRenderCb = () => {
                    glCtx.drawImage(canvas, 0, 0)
                    next(nextSibling, done => {
                        parentNode.removeChild(canvas)

                        requestAnimationFrame(() => {
                            this.nextRenderCb = () => {
                                const dataUrl = canvas.toDataURL(format, quality)
                                canvas.width = oriWidth
                                canvas.height = oriHeight
                                resize(oriWidth, oriHeight)
                                requestAnimationFrame(() => {
                                    parentNode.insertBefore(canvas, nextSibling)
                                    done(dataUrl)
                                })
                            }
                            canvas.width = width
                            canvas.height = height
                            resize(width, height)
                        })
                    })
                }
            } else {
                next(false, done => {
                    this.nextRenderCb = () => {
                        const dataUrl = canvas.toDataURL(format, quality)
                        done(dataUrl)
                    }
                })
            }
        }

        const render = renderer.render.bind(renderer)

        renderer.render = function (scene, time) {
            render(scene, time)

            if (this.nextRenderCb) {
                this.nextRenderCb()
                this.nextRenderCb = undefined
            }
        }

        return renderer
    }

    _modifyScene(scene) {
        const self = this
        scene.addAvatarAsync = async function (
            controller_config_path,
            bundleConfig,
            graphJson = animGraphJson,
            logicJson = animLogicJson,
            avatarJson,
        ) {
            const sceneSelf = this
            const controllConfig = await self._resourceManager.getControllConfig()

            const controller_config_promise = self.LoadAssets(controllConfig)

            if (!bundleConfig) {
                bundleConfig = avatarJson.gender === 'male' ? maleDefaults : femaleDefaults
            }

            const privilegedBundles = []
            if (bundleConfig.Default && bundleConfig.Default.length) {
                for (let bundle of bundleConfig.Default) {
                    privilegedBundles.push(bundle)
                }
            }

            // default 关联 bundle 一次性载入
            const requests = []
            const stringList = self.createStringList()
            for (let path of privilegedBundles) {
                requests.push(self._resourceManager.prepareData(path, true))
                stringList.push_back(path)
            }
            await Promise.all(requests)

            const avt = self._modifyAvatar(
                this.addAvatarWithBundleConfig(JSON.stringify(bundleConfig)),
                sceneSelf,
            )
            // controller_config
            const controller_config = await controller_config_promise
            avt.setConfigWithData(controller_config.ptr, controller_config.size)
            self.wasmCtx.module._free(controller_config.ptr)
            // item_list
            // avt.addItemList(JSON.stringify(itemListJson));
            // anime_graph
            const anime_graph = self.writeUTF8String(JSON.stringify(graphJson))
            avt.setAnimGraph(anime_graph.ptr, anime_graph.size)
            avt.useDefaultAnimationGraph(true)
            self.wasmCtx.module._free(anime_graph.ptr)
            // anime_logic
            const anime_logic = self.writeUTF8String(JSON.stringify(logicJson))
            avt.setAnimLogic(anime_logic.ptr, anime_logic.size)
            avt.useDefaultAnimationLogic(1)
            self.wasmCtx.module._free(anime_logic.ptr)
            // avatar_json
            const { bundle_list, color_list, facepup_config } = avatarJson
            // 换装

            const largeBundles = []
            const normalBundles = []

            for (let bundle of bundle_list) {
                if (privilegedBundles && privilegedBundles.indexOf(bundle) !== -1) {
                    continue
                } else if (/*bundle.includes('BaseModel') || */ bundle.includes('hair')) {
                    largeBundles.push(bundle)
                } else {
                    normalBundles.push(bundle)
                }
            }

            let insertIndex = largeBundles.length - 1
            const largeJobs = []
            for (let i = 0; i < largeBundles.length; i++) {
                const bundle = largeBundles[i]

                const job = self._resourceManager.prepareData(
                    bundle,
                    false,
                    i === insertIndex
                        ? () => {
                              avt.addComponentsSync(stringList, privilegedBundles.length)
                              self._modifiedRenderer.updateForRender(sceneSelf, 0)
                          }
                        : undefined,
                )
                largeJobs.push(job)
            }
            const largsPaths = await Promise.all(largeJobs)

            const normalJobs = []
            for (let i = 0; i < normalBundles.length; i++) {
                const bundle = normalBundles[i]

                const job = self._resourceManager
                    .prepareData(
                        bundle,
                        false,
                        i === 0
                            ? () => {
                                  for (let largePath of largsPaths) {
                                      const pathList = self.createStringList()
                                      pathList.push_back(largePath)
                                      avt.addComponentsSync(pathList, 1)
                                      self._modifiedRenderer.updateForRender(sceneSelf, 0)
                                  }
                              }
                            : undefined,
                    )
                    .then(() => {
                        const pathList = self.createStringList()
                        pathList.push_back(bundle)
                        avt.addComponentsSync(pathList, 1)
                        self._modifiedRenderer.updateForRender(sceneSelf, 0)
                    })
                normalJobs.push(job)
            }

            await Promise.all(normalJobs)
            if (bundle_list.length) {
                // 设置颜色和捏脸数据
                for (let colorObj of color_list) {
                    const name = colorObj.name
                    const color = colorObj.color
                    avt.setColor(name, color)
                }
                for (let bone_control_obj of facepup_config.bone_controllers) {
                    const name = bone_control_obj.name
                    const value = bone_control_obj.value
                    avt.setBoneTransform(name, value)
                }
            }
            self._resourceManager.markFilesUsed(bundle_list)

            return avt
        }

        scene.prepareAvatarAsync = async function (
            controller_config_path,
            itemListJson,
            avatarJson,
        ) {
            await self._resourceManager.prepareData(controller_config_path, true)
            const requests = []
            for (let bundle of avatarJson.bundle_list) {
                requests.push(self._resourceManager.prepareData(bundle))
            }
            await Promise.all(requests)
        }

        scene.setForegroundAsync = async function (path) {
            await self._resourceManager.prepareData(path)

            const result = this.setForegroundSync(path)
            self._resourceManager.markFileUsed(path)

            return result
        }

        scene.setBackgroundAsync = async function (path) {
            await self._resourceManager.prepareData(path)

            const result = this.setBackgroundSync(path)
            self._resourceManager.markFileUsed(path)

            return result
        }

        scene.setLightAsync = async function (path) {
            await self._resourceManager.prepareData(path)

            const result = this.setLightSync(path)
            self._resourceManager.markFileUsed(path)

            return result
        }

        scene.setCameraAsync = async function (path) {
            await self._resourceManager.prepareData(path)

            const result = this.setCameraSync(path)
            self._resourceManager.markFileUsed(path)

            return result
        }

        return scene
    }

    _modifyAvatar(avatar, scene) {
        const self = this
        avatar.setAnimationAsync = async function (path) {
            await self._resourceManager.prepareData(path)

            const result = this.setAnimationSync(path)
            self._resourceManager.markFileUsed(path)

            return result
        }

        avatar.setDefaultStateAnimationAsync = async function (path, isLoop = true) {
            await self._resourceManager.prepareData(path)

            const result = this.setDefaultStateAnimationSync(path, isLoop ? 1 : 0)
            self._resourceManager.markFileUsed(path)

            return result
        }

        avatar.addComponentsAsync = async function (paths, pathCount) {
            const requests = []
            const result = []
            for (let path of paths) {
                const stringList = self.createStringList()
                stringList.push_back(path)
                const p = self._resourceManager.prepareData(path).then(() => {
                    const removeList = this.addComponentsSync(stringList, 1)
                    self._modifiedRenderer.updateForRender(scene, 0)
                    self._resourceManager.markFilesUsed(paths)
                    const size = removeList.size()
                    for (let i = 0; i < size; i++) {
                        result.push(removeList.get(i))
                    }
                })
                requests.push(p)
            }

            return Promise.all(requests).then(() => result)
        }

        avatar.prepareComponentsAsync = async function (paths, isPersistent) {
            const requests = []
            for (let path of paths) {
                requests.push(self._resourceManager.prepareData(path, isPersistent))
            }
            await Promise.all(requests)
        }

        avatar.setAvatarMouthBlendShape = function (expression) {
            const module = self.wasmCtx.module
            const heapSpace = module._malloc(expression.length * expression.BYTES_PER_ELEMENT)
            module.HEAPF32.set(expression, heapSpace >> 2) // 2
            this.blendMouthShape(heapSpace, 47)
            module._free(heapSpace)
        }

        return avatar
    }
}
